import { useState } from "react";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { IoMdArrowBack } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import BeatLoader from "react-spinners/BeatLoader";
import { sendForgotPasswordOtp } from "../redux/ApiSlice/ForgotPasswordSlice";
import { buildIdentifierFields } from "../utils/identifier";
import {
    isPhoneIdentifierValue,
    phoneLocalPart,
    resolveEmailOrPhoneInput,
    normalizeIdentifier,
} from "../utils/phoneInput";

const ForgotPasswordPage = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [identifier, setIdentifier] = useState("");
    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    // e.target.value here is the LOCAL input's own text — it never contains "+1" (that's a
    // static sibling span, see the JSX), so there's no prefix for the caret to get lost inside.
    const handleIdentifierChange = (e) => {
        if (error) setError("");
        const oldLocal = phoneLocalPart(identifier);
        setIdentifier(resolveEmailOrPhoneInput(oldLocal, e.target.value));
    };

    const handleSendOtp = async (finalIdentifier) => {
        setIsLoading(true);
        try {
            // Only the relevant field carries a value; the unused one is sent empty so the
            // backend never sees a phone number when an email was entered, or vice versa.
            const { email: emailField, phone: phoneField } = buildIdentifierFields(finalIdentifier);
            const formData = new FormData();
            formData.append("email", emailField ?? "");
            formData.append("phone", phoneField ?? "");
            const response = await dispatch(sendForgotPasswordOtp(formData));
            if (response?.type === "sendForgotPasswordOtp/fulfilled") {
                toast.success(response?.payload?.message);
                setIdentifier("");
                setTimeout(() => {
                    setIsLoading(false);
                    navigate(`/enterotp/${finalIdentifier}`);
                }, 2000);
            } else if (response?.payload?.response) {
                const fieldErrors = response.payload.response.data;
                const message = fieldErrors?.email?.[0] || fieldErrors?.phone?.[0] || "Something went wrong.";
                toast.error(message);
                setIsLoading(false);
            } else {
                setIsLoading(false);
            }
        } catch (error) {
            toast.error(error.message);
            setIsLoading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const trimmed = identifier.trim();
        if (!trimmed) {
            setError("Required");
            return;
        }

        if (isPhoneIdentifierValue(trimmed)) {
            if (phoneLocalPart(trimmed).replace(/\D/g, "").length !== 10) {
                setError("Enter a valid 10-digit phone number");
                return;
            }
            setError("");
            handleSendOtp(normalizeIdentifier(trimmed));
            return;
        }

        if (!/\S+@\S+\.\S+/.test(trimmed)) {
            setError("Invalid email");
            return;
        }
        setError("");
        handleSendOtp(trimmed);
    };

    return (
        <div className="h-full w-full bg-white dark:bg-[#1E1D21]">
            <div className="sticky top-0">
                <div className="relative flex items-center justify-center h-12 bg-[#BDE6FF] rounded-b-xl px-2 z-10">
                    <IoMdArrowBack
                        onClick={() => navigate("/login")}
                        className="absolute left-2 top-1/2 transition-transform -translate-y-1/2 text-[24px] text-black cursor-pointer"
                    />
                    <p className="text-center font-bold text-[16px] text-black flex-grow">Forgot password</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="w-full h-full p-4">
                <p className="text-[18px] font-semibold text-black dark:text-white">
                    Reset your password
                </p>
                <p className="text-[11px] mt-1 font-semibold text-gray-500 dark:text-gray-400">
                    Enter the email or phone number linked to your account.
                </p>

                <div className="px-4 pt-4 pb-3 rounded-xl border-2 border-[#BDE6FF] mt-4">
                    <p className="text-[12px] font-semibold text-black dark:text-white">Email or phone number</p>
                    <div className="flex items-center gap-1">
                        {isPhoneIdentifierValue(identifier) && (
                            <span className="text-[12px] text-black dark:text-white font-bold select-none">+1</span>
                        )}
                        <input
                            type="text"
                            value={phoneLocalPart(identifier)}
                            onChange={handleIdentifierChange}
                            placeholder="Enter your email or phone number"
                            className="w-full text-[12px] focus:outline-none bg-white dark:bg-[#1E1D21] text-black dark:text-white"
                        />
                    </div>
                    {error && <p className="text-red-500 text-[11px] mt-1">{error}</p>}
                </div>

                <button
                    type="submit"
                    disabled={isLoading}
                    className="h-12 text-[16px] font-semibold px-3 bg-[#BDE6FF] text-black rounded-xl w-full my-6 disabled:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {isLoading ? <BeatLoader
                        size={10}
                        color={"#fff"}
                        loading={isLoading}
                    /> : "Send Code"
                    }
                </button>
                <p
                    onClick={() => navigate("/login")}
                    className="text-[12px] text-center underline text-[#0A96C1] dark:text-[#0da7d6] font-semibold cursor-pointer"
                >
                    Back to log in
                </p>
            </form>
        </div>
    );
};

export default ForgotPasswordPage;
